import { Download, OpenInNew } from '@mui/icons-material';
import { Grid, Button } from '@mui/material';

import { VALID_MS_EMBED_FORMATS } from '@/utils/constants';

interface MsDocumentViewerProps {
  url: string;
}

const MsDocumentViewer: React.FC<MsDocumentViewerProps> = ({ url }) => {
  const getEmbedUrl = (url: string): string => {
    if (!url) return '';

    if (VALID_MS_EMBED_FORMATS.includes(getFileType(url))) {
      return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
    }

    return url;
  };

  const getFileType = (fileUrl: string): string => {
    if (!fileUrl) return '';

    const extension = fileUrl.split('?')?.shift()?.split('.').pop()?.toLowerCase();

    switch (extension) {
      case 'pdf':
        return 'pdf';
      case 'xlsx':
      case 'xls':
        return 'excel';
      case 'docx':
      case 'doc':
        return 'word';
      case 'pptx':
      case 'ppt':
        return 'powerpoint';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'webp':
        return 'image';
      default:
        return 'document';
    }
  };

  const currentFileType = getFileType(url);

  if (!url) return <div>No file URL provided</div>;

  const renderContent = () => {
    switch (currentFileType) {
      case 'pdf':
        return (
          <iframe
            src={url}
            width="100%"
            height="600"
            title="PDF Document"
            className="w-full"
            onError={(e) => console.error('Failed to load PDF:', e)}
          >
            <p>Your browser does not support PDF viewing.</p>
            <a href={url} target="_blank" rel="noopener noreferrer">
              Open PDF in new window
            </a>
          </iframe>
        );

      case 'image':
        return (
          <img
            src={url}
            alt="Document Image"
            style={{ maxWidth: '100%', maxHeight: '600px', objectFit: 'contain' }}
            onError={(e) => console.error('Failed to load image:', e)}
          />
        );

      default:
        if (VALID_MS_EMBED_FORMATS.includes(currentFileType)) {
          return (
            <iframe
              src={getEmbedUrl(url)}
              width="100%"
              height="600"
              title={`Embedded ${currentFileType} file`}
              className="w-full"
              onError={(e) => console.error('Failed to load iframe:', e)}
            >
              <p>Your browser does not support iframes.</p>
              <a href={getEmbedUrl(url)} target="_blank" rel="noopener noreferrer">
                Open file in new window
              </a>
            </iframe>
          );
        } else {
          return (
            <div style={{ padding: '20px', textAlign: 'center' }}>
              <p>This file type ({currentFileType}) cannot be previewed.</p>
            </div>
          );
        }
    }
  };

  return (
    <Grid container p={2} direction="column" alignItems="center" size={12}>
      {renderContent()}
      <Grid container gap={1} justifyContent="center" sx={{ mt: 2 }}>
        <Button
          variant="contained"
          startIcon={<Download />}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          download
        >
          Download
        </Button>
        <Button variant="outlined" startIcon={<OpenInNew />} href={url} target="_blank" rel="noopener noreferrer">
          Open in new window
        </Button>
      </Grid>
    </Grid>
  );
};

export default MsDocumentViewer;
